(ns storrito.editor.music-modal.preview
  "Pure view helpers shared by the search and section views of the
   music modal: formatters, the cover thumbnail, the CSS equalizer, the
   accent styles and the section-duration math. No state and no
   side-effects — the interactive state lives in the store under
   `[:ui/modal :music]` and is mutated by the registered `:music/*`
   action handlers (see ui.editor2.modal-music).")

(def min-section-ms 1000)

(def preview-window-ms
  "The Instagram catalog has no dedicated preview-clip URL, so the
   player previews a bounded window of the full song and loops it,
   rather than playing the whole track."
  30000)

;; A single neutral accent used across the search and section views
;; (active pills/chips, the round player buttons), so both share one
;; look:
(def accent-solid
  "bg-gray-800 text-white border-gray-800")

(def accent-round
  "bg-gray-800 hover:bg-gray-700 text-white")

(defn format-duration
  [duration-ms]
  (when duration-ms
    (let [total-seconds (js/Math.round (/ duration-ms 1000))
          minutes (js/Math.floor (/ total-seconds 60))
          seconds (mod total-seconds 60)]
      (str minutes ":" (when (< seconds 10) "0") seconds))))

(defn format-section
  "Formats a section duration as seconds, with one decimal for custom
   durations like \"7.4s\"."
  [ms]
  (if (zero? (mod ms 1000))
    (str (js/Math.round (/ ms 1000)) "s")
    (str (.toFixed (/ ms 1000) 1) "s")))

(defn max-section-ms
  [music track]
  (min (:section-length-ms music 15000)
       (:duration_in_ms track)))

(defn section-options
  "The selectable section durations, mirroring Instagram's 5s/10s/15s
   choices, capped by the slide duration and the song length."
  [music track]
  (let [max-ms (max-section-ms music track)]
    (vec
      (distinct
        (conj (filterv #(< % max-ms)
                       [5000 10000 15000])
              max-ms)))))

(defn default-section-ms
  [music track]
  (min 15000
       (max-section-ms music track)))

(defn clamp-start-ms
  [track section-ms start-ms]
  (let [max-start (max 0 (- (:duration_in_ms track)
                            section-ms))]
    (js/Math.round (max 0 (min max-start start-ms)))))

(defn drag-selection
  "The new {:start-ms :section-ms} for a section-picker drag, given the
   `mode` (:move / :resize-left / :resize-right), the current `sel`
   {:track :start-ms :section-ms}, `music` (for the section caps), the
   song position `ms` under the cursor, and — for :move — `anchor-ms`,
   the offset of the grabbed point from the window start (so the grabbed
   point stays under the cursor). Pure, so the imperative drag in the
   section view and the store commit share one source of truth."
  [mode {:keys [track start-ms section-ms]} music ms anchor-ms]
  (let [duration (max 1 (:duration_in_ms track))]
    (case mode
      :move
      {:start-ms (clamp-start-ms track section-ms (- ms anchor-ms))
       :section-ms section-ms}

      :resize-right
      {:start-ms start-ms
       :section-ms (-> (- ms start-ms)
                       (max min-section-ms)
                       (min (max-section-ms music track))
                       (min (- duration start-ms))
                       (js/Math.round))}

      :resize-left
      (let [end-ms (+ start-ms section-ms)
            new-start (-> ms
                          (max 0)
                          (max (- end-ms (max-section-ms music track)))
                          (min (- end-ms min-section-ms))
                          (js/Math.round))]
        {:start-ms new-start
         :section-ms (- end-ms new-start)})

      {:start-ms start-ms :section-ms section-ms})))

(defn preview-playing?
  "Whether the given track is the one currently playing in the player."
  [music track]
  (let [{:keys [track-id playing?]} (:preview music)]
    (and playing?
         (= track-id (:id track)))))

(defn cover-thumbnail
  [track size-px]
  (if (seq (:cover_artwork_thumbnail_uri track))
    [:img.rounded
     {:style {:width (str size-px "px")
              :height (str size-px "px")
              :object-fit "cover"}
      :src (:cover_artwork_thumbnail_uri track)}]
    [:i.fas.fa-music.text-lg.text-gray-400]))

(def equalizer-css
  "A CSS three rounded-capsule equalizer that mirrors the animation
   baked into the music sticker (waveform-bounce.webm): the capsules
   grow/shrink from their vertical center, the outer two move in sync
   while the middle one is in antiphase, at the measured 1.16s period.
   Keyframes (not a looping <video>, which does not always autoplay).
   currentColor, sized to the surrounding text (1em)."
  (str
    ".mm-eq{display:inline-flex;align-items:center;gap:1.5px;"
    "height:1em;width:0.8em}"
    ".mm-eq>span{flex:1 1 0;height:100%;background:currentColor;border-radius:99px;"
    "transform-origin:center;animation:mm-eq 1.16s ease-in-out infinite}"
    ;; The middle capsule is half a period out of phase (antiphase);
    ;; nth-of-type (not nth-child) so the injected <style> sibling does
    ;; not shift the count:
    ".mm-eq>span:nth-of-type(2){animation-delay:-0.58s}"
    "@keyframes mm-eq{0%,100%{transform:scaleY(0.4)}50%{transform:scaleY(0.9)}}"))

(defn playing-indicator
  "A three-bar equalizer animation shown left of the title of the
   currently playing song, sized to the title text."
  []
  [:span.mm-eq.mr-1.shrink-0.text-gray-700
   {:aria-hidden true}
   [:style {:dangerouslySetInnerHTML {:__html equalizer-css}}]
   [:span]
   [:span]
   [:span]])
